import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ChevronLeft, ChevronRight, Copy } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { optionalDay, optionalOneOf, optionalString, KEEP_PLACE } from "@/lib/search";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import {
  menuListOrderAsk,
  menuOpeningDay,
  menuWeekDays,
  menuWeekStart,
  shiftDay,
} from "@alm/domain";
import { ShoppingAddOnDialog } from "./-components/shopping-add-on-dialog";
import { ShoppingAddOns } from "./-components/shopping-add-ons";
import { OrderWithNancy } from "@/components/app/clinical/order-with-nancy";
import { ShoppingMenuLines } from "./-components/shopping-menu-lines";
import { ShoppingOptimized } from "./-components/shopping-optimized";
import { useBuildingToday } from "@/hooks/use-facility-today";
import { dateLabel, dayLabel } from "./-lib/days";
import { useKitchen } from "./-lib/use-kitchen";

type ShoppingSearch = { span?: "day"; on?: string; extra?: string; view?: "combined" };

export const Route = createFileRoute("/_authenticated/menus/shopping-list")({
  validateSearch: (search: Record<string, unknown>): ShoppingSearch => ({
    span: optionalOneOf(["day"] as const, search.span),
    on: optionalDay(search.on),
    extra: optionalString(search.extra)?.slice(0, 6) || undefined,
    view: optionalOneOf(["combined"] as const, search.view),
  }),
  component: ShoppingListPage,
});

/** The list was refused because no resident is in care on those days. */
const noResidents = (error: unknown): boolean =>
  (error as { data?: { details?: { field?: unknown } } }).data?.details?.field === "residents";

/** What to buy for a day or a week: what the kitchen added by hand, then the menu, each dish for
    its share of who is eating, in the forms the kitchen buys, less the shelf. Both follow the days
    chosen and both are copied. */
function ShoppingListPage() {
  const { facilityId, canManage } = useKitchen();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ShoppingSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const today = useBuildingToday(facilityId);
  const span: "day" | "week" = params.span ?? "week";
  const setSpan = (next: "day" | "week") => patch({ span: next === "day" ? next : undefined });
  const opening = menuOpeningDay(today);
  const anchor = params.on ?? opening;
  const setAnchor = (next: string) => patch({ on: next === opening ? undefined : next });
  const extra = params.extra ?? "";
  const setExtra = (next: string) => patch({ extra: next || undefined });
  const view: "planned" | "combined" = params.view ?? "planned";
  const setView = (next: "planned" | "combined") =>
    patch({ view: next === "combined" ? next : undefined });
  const from = span === "week" ? menuWeekStart(anchor) : anchor;
  const to = span === "week" ? shiftDay(from, 6) : from;
  const extraDiners = Math.max(0, Number(extra) || 0);
  const list = useQuery({
    ...orpc.agents.menus.shoppingList.queryOptions({
      input: { facilityId, from, to, extraDiners },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  // Combining asks a model, so it runs only once someone opens that view.
  const combined = useQuery({
    ...orpc.agents.menus.shoppingListOptimized.queryOptions({
      input: { facilityId, from, to, extraDiners },
    }),
    enabled: view === "combined",
    placeholderData: keepWhileSame({ facilityId }),
  });
  const step = span === "week" ? 7 : 1;
  const days = span === "week" ? menuWeekDays(from) : [from];
  const range = span === "week" ? `${dateLabel(from)} – ${dateLabel(to)}` : dayLabel(from);
  const toBuy = list.data?.lines.filter((line) => line.toBuy.quantity > 0) ?? [];
  const addOns = list.data?.addOns ?? [];

  const showCombined = view === "combined" && combined.data !== undefined;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        (showCombined ? combined.data?.text : list.data?.text) ?? "",
      );
    } catch {
      toast.error("Couldn't copy the list. Allow clipboard access for this site and try again.");
      return;
    }
    const fromMenu = showCombined
      ? `${String(combined.data?.items.length ?? 0)} items for ${String(toBuy.length)} lines`
      : `${String(toBuy.length)} from the menu`;
    toast.success(`Shopping list for ${range} copied`, {
      description: [fromMenu, `${String(addOns.length)} added by hand`].join(", "),
    });
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <ToggleGroup
          type="single"
          variant="outline"
          value={span}
          onValueChange={(value) => value && setSpan(value as "day" | "week")}
        >
          <ToggleGroupItem value="day">Day</ToggleGroupItem>
          <ToggleGroupItem value="week">Week</ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup
          type="single"
          variant="outline"
          aria-label="How the list is shown"
          value={view}
          onValueChange={(value) => value && setView(value as "planned" | "combined")}
        >
          <ToggleGroupItem value="planned">As planned</ToggleGroupItem>
          <ToggleGroupItem value="combined">Combined</ToggleGroupItem>
        </ToggleGroup>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={span === "week" ? "Previous week" : "Previous day"}
            onClick={() => setAnchor(shiftDay(anchor, -step))}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="min-w-40 text-center text-sm font-medium tabular-nums">{range}</span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={span === "week" ? "Next week" : "Next day"}
            onClick={() => setAnchor(shiftDay(anchor, step))}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
        <div className="space-y-1">
          <Label htmlFor="extra-diners" className="text-xs">
            Staff or guests each day
          </Label>
          <Input
            id="extra-diners"
            value={extra}
            inputMode="numeric"
            placeholder="0"
            className="h-8 w-24 tabular-nums"
            onChange={(event) => setExtra(event.target.value)}
          />
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canManage ? (
            <ShoppingAddOnDialog
              facilityId={facilityId}
              days={days}
              initialDay={days.includes(today) ? today : from}
            />
          ) : null}
          <Button
            variant="outline"
            size="sm"
            disabled={toBuy.length === 0 && addOns.length === 0}
            onClick={() => void copy()}
          >
            <Copy className="size-3.5" />
            Copy list
          </Button>
          <OrderWithNancy
            facilityId={facilityId}
            askFor={(building) => menuListOrderAsk({ from, to, extraDiners, building })}
            disabled={
              list.isPending ||
              list.isPlaceholderData ||
              (toBuy.length === 0 && addOns.length === 0)
            }
          />
        </div>
      </div>

      {list.isPending ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : list.isError && noResidents(list.error) ? (
        <EmptyState
          title="No residents in care on these days"
          description="The shopping list is worked out for the residents in care each day. Add the residents in Residents, or put staff or guests in the extra people box."
        />
      ) : list.isError ? (
        <ReadFailed
          message="Couldn't work out the list."
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      ) : list.data.dishes === 0 && addOns.length === 0 ? (
        <EmptyState
          title="Nothing to buy for these days"
          description={
            canManage
              ? "The list is worked out from the menu. Plan some meals on the calendar, or use Add item for something no dish needs."
              : "The list is worked out from the menu. Plan some meals on the calendar first."
          }
          action={
            <Button asChild variant="outline">
              <Link to="/menus/calendar">Open the calendar</Link>
            </Button>
          }
        />
      ) : (
        <div className="space-y-6">
          {addOns.length > 0 ? (
            <ShoppingAddOns
              facilityId={facilityId}
              canManage={canManage}
              oneDay={days.length === 1}
              addOns={addOns}
            />
          ) : null}
          {view === "planned" || list.data.dishes === 0 ? (
            <ShoppingMenuLines
              dishes={list.data.dishes}
              lines={list.data.lines}
              portions={list.data.servings}
            />
          ) : combined.isPending ? (
            <Skeleton className="h-64 rounded-xl" />
          ) : combined.isError ? (
            <ReadFailed
              message="Couldn't combine the list."
              error={combined.error}
              onRetry={() => void combined.refetch()}
            />
          ) : (
            <ShoppingOptimized
              planned={combined.data.planned}
              items={combined.data.items}
              note={combined.data.note}
            />
          )}
        </div>
      )}
    </section>
  );
}
